import React, { useEffect, useState } from "react";
import { Modal, Button, Alert, Spinner, Form } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";
import OutstandingGrid from "./OutstandingGrid";

interface Props {
  show: boolean;
  onHide: () => void;
  enrollmentId: number;
  availableCredit: number;
  onSuccess: () => void;
}

const AdjustCreditModal: React.FC<Props> = ({ show, onHide, enrollmentId, availableCredit, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [installments, setInstallments] = useState<any[]>([]);
  const [amounts, setAmounts] = useState<Record<string, number>>({});
  const [remarks, setRemarks] = useState("");

  // Keep the modal open after success so the auto-generated receipt number is
  // visible; the parent's onSuccess (refresh + close) fires on close.
  const handleClose = () => {
    if (successMsg) onSuccess();
    else onHide();
  };

  useEffect(() => {
    if (show) {
      setError(null);
      setSuccessMsg(null);
      setAmounts({});
      setRemarks("");
      setLoading(true);
      EnrollmentService.getOutstanding(enrollmentId)
        .then((res: any) => {
          if (res.data?.success) {
            setInstallments(res.data.data.installments || []);
          } else {
            setError(res.data?.message || "Failed to load outstanding");
          }
        })
        .catch((err: any) => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [show, enrollmentId]);

  const handleAmountChange = (installmentId: number, headId: number, amount: number) => {
    setAmounts((prev) => ({ ...prev, [`${installmentId}-${headId}`]: amount }));
  };

  const totalEntered = Object.values(amounts).reduce((s, v) => s + (v || 0), 0);

  const handleSubmit = async () => {
    setError(null);
    setSuccessMsg(null);

    const heads: any[] = [];
    for (const [key, amt] of Object.entries(amounts)) {
      if (amt > 0) {
        const [instId, headId] = key.split("-").map(Number);
        heads.push({ installmentId: instId, installmentHeadId: headId, amount: amt });
      }
    }

    if (heads.length === 0) {
      setError("Enter at least one amount to adjust");
      return;
    }

    if (totalEntered > availableCredit) {
      setError(`Total (${totalEntered.toLocaleString()}) exceeds available credit (${availableCredit.toLocaleString()})`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await EnrollmentService.adjustCredit(enrollmentId, {
        amount: totalEntered,
        heads,
        remarks,
      });
      if (res.data?.success) {
        setSuccessMsg(
          `Credit of ${totalEntered.toLocaleString()} adjusted.${
            res.data.data?.autoReceiptNumber
              ? ` Receipt: ${res.data.data.autoReceiptNumber}`
              : ""
          }`,
        );
      } else {
        setError(res.data?.message || "Failed to adjust credit");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to adjust credit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Adjust Credit Against Outstanding</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 13 }}>
        <div style={{ marginBottom: 10, padding: "6px 10px", background: "var(--status-success-bg)", borderRadius: 4, fontSize: 12 }}>
          Available Credit: <b style={{ color: "var(--status-success)" }}>{availableCredit.toLocaleString()}</b>
          {totalEntered > 0 && (
            <span style={{ marginLeft: 12 }}>
              Adjusting: <b style={{ color: totalEntered > availableCredit ? "var(--status-danger)" : "var(--status-info)" }}>
                {totalEntered.toLocaleString()}
              </b>
            </span>
          )}
        </div>

        {error && <Alert variant="danger" style={{ fontSize: 12 }}>{error}</Alert>}
        {successMsg && <Alert variant="success" style={{ fontSize: 12 }}>{successMsg}</Alert>}

        {loading ? (
          <div style={{ textAlign: "center", padding: 30 }}>
            <Spinner role="status" animation="border" size="sm" /> Loading outstanding...
          </div>
        ) : (
          <>
            <OutstandingGrid
              installments={installments}
              amounts={amounts}
              onAmountChange={handleAmountChange}
            />

            <Form.Group className="mb-2" style={{ marginTop: 8 }}>
              <Form.Label style={{ fontSize: 12 }}>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {successMsg ? (
          <Button variant="secondary" size="sm" onClick={handleClose}>Close</Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={onHide}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleSubmit} disabled={submitting || loading}>
              {submitting ? <><Spinner role="status" animation="border" size="sm" /> Adjusting...</> : "Adjust Credit"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AdjustCreditModal;
